import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMerchant } from '../hooks/useMerchant.js';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead } from '../components/ui.js';
import { Icon } from '../components/Icon.js';

const UPSELL = ['off', 'low', 'medium', 'high'] as const;
const SPEED  = ['instant', 'natural', 'slow'] as const;
type Upsell = typeof UPSELL[number];
type Speed  = typeof SPEED[number];

const VOICE_PRESETS_AR = [
  { labelKey: 'page.voice.preset.friendly', value: 'تحدّث بشكل ودود وعفوي باللهجة العراقية. استخدم الإيموجي بحدود. لا تتكلّف الرسمي.' },
  { labelKey: 'page.voice.preset.luxury',   value: 'استخدم نبرة راقية وكلاماً مهذّباً. لا تستخدم الإيموجي. خاطب العميل باحترام.' },
  { labelKey: 'page.voice.preset.fast',     value: 'ردود قصيرة ومباشرة، بلا حشو. أسئلة من سطر واحد. لا تطيل الشرح.' },
];

const VOICE_PRESETS_EN = [
  { labelKey: 'page.voice.preset.friendly', value: 'Friendly, casual tone. Use first-person. Light emoji OK. Avoid formal language.' },
  { labelKey: 'page.voice.preset.luxury',   value: 'Refined, polished tone. No emoji. Address the customer with respect.' },
  { labelKey: 'page.voice.preset.fast',     value: 'Short, direct replies. One-line questions. No fluff.' },
];

export function BrandVoice() {
  const { data: merchant } = useMerchant();
  const api = useApi();
  const qc = useQueryClient();
  const { t, lang } = useLang();

  const [brandVoice, setBrandVoice] = useState('');
  const [upsell, setUpsell] = useState<Upsell>('off');
  const [speed, setSpeed] = useState<Speed>('natural');

  useEffect(() => {
    if (merchant) {
      setBrandVoice(merchant.brand_voice);
      setUpsell(merchant.upsell_level);
      setSpeed(merchant.response_speed);
    }
  }, [merchant]);

  const save = useMutation({
    mutationFn: () => api.patch('/merchants/me', {
      brand_voice: brandVoice, upsell_level: upsell, response_speed: speed,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant'] }),
  });

  const presets = lang === 'ar' ? VOICE_PRESETS_AR : VOICE_PRESETS_EN;

  return (
    <div className="wrap section-gap">
      <PageHead
        title={t('page.voice.title')}
        sub={t('page.voice.sub')}
        actions={
          <button className="btn btn--primary" disabled={save.isPending} onClick={() => save.mutate()}>
            <Icon name="check" />{save.isPending ? t('common.saving') : t('common.save')}
          </button>
        }
      />

      <div className="card card--pad">
        <div className="field">
          <label className="field__label">{t('page.voice.instr')}</label>
          <div className="tmpls">
            {presets.map((p) => (
              <button key={p.labelKey} type="button" className="tmpl" onClick={() => setBrandVoice(p.value)}>
                {t(p.labelKey)}
              </button>
            ))}
          </div>
          <textarea
            className="textarea"
            rows={8}
            value={brandVoice}
            onChange={(e) => setBrandVoice(e.target.value)}
            placeholder={t('page.voice.instr.ph')}
          />
          <div className="field__hint">{t('page.voice.instr.hint')}</div>
        </div>

        <div className="field">
          <label className="field__label">{t('page.voice.upsell')}</label>
          <div className="seg" style={{ flexWrap: 'wrap' }}>
            {UPSELL.map((u) => (
              <button key={u} className={upsell === u ? 'on' : ''} onClick={() => setUpsell(u)}>
                {t(`voice.upsell.${u}`)}
              </button>
            ))}
          </div>
          <div className="field__hint">{t(`voice.upsell.hint.${upsell}`)}</div>
        </div>

        <div className="field" style={{ marginBottom: 0 }}>
          <label className="field__label">{t('page.voice.speed')}</label>
          <div className="seg">
            {SPEED.map((s) => (
              <button key={s} className={speed === s ? 'on' : ''} onClick={() => setSpeed(s)}>
                {t(`voice.speed.${s}`)}
              </button>
            ))}
          </div>
          <div className="field__hint">{t(`voice.speed.hint.${speed}`)}</div>
        </div>
      </div>
    </div>
  );
}
